import { useFeatureFlagEnabled, useFeatureFlagVariantKey } from 'posthog-js/react'

import { env } from '@percent/partner-dashboard/env'
import type { PostHogFeatureFlag } from './posthogFeatureFlags'

// Local-only flag overrides: a JSON map of flag key -> boolean from
// `NX_PUBLIC_FEATURE_FLAG_OVERRIDES` (set only in the gitignored `.env.local`). Parsed once,
// applied with highest precedence so any flag can be forced on/off for local testing without a
// PostHog project. It's absent from all committed/deployed config, so it can't affect a real
// environment; malformed JSON or non-boolean values are ignored.
const parseFlagOverrides = (raw: string | undefined): Partial<Record<string, boolean>> => {
  if (!raw) return {}

  try {
    const parsed: unknown = JSON.parse(raw)

    if (typeof parsed !== 'object' || parsed === null) return {}

    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(([, value]) => typeof value === 'boolean')
    ) as Record<string, boolean>
  } catch {
    return {}
  }
}

// Development-only: never applied under test, where PostHog is stubbed via
// `mockPostHogFeatureFlags` and the suite must stay deterministic regardless of a developer's
// local `.env.local` (which Nx loads into the test process).
const FLAG_OVERRIDES =
  env.NX_PUBLIC_REACT_APP_ENV === 'test' ? {} : parseFlagOverrides(env.NX_PUBLIC_FEATURE_FLAG_OVERRIDES)

export function usePostHogFeatureFlag(key: PostHogFeatureFlag): boolean {
  const remoteValue = useFeatureFlagEnabled(key)
  const override = FLAG_OVERRIDES[key]

  // A local override (from `.env.local`) wins, so a flag can be exercised without PostHog.
  if (override !== undefined) return override

  return remoteValue ?? false
}

export function usePostHogFeatureFlagVariant(key: PostHogFeatureFlag): string | boolean | undefined {
  return useFeatureFlagVariantKey(key)
}
